import React, { lazy, Suspense } from 'react';
import { Switch, Route, Redirect } from 'wouter';
import { TenantProvider } from './lib/tenant-context.js';
import { useAuth } from './hooks/useAuth.js';
import { useEmbeddedMode } from './hooks/useEmbeddedMode.js';
import { TooltipProvider } from './components/ui/tooltip.js';
import AppShell from './components/shell/app-shell.js';
import BrowserCallProvider from './providers/BrowserCallProvider.js';
import ConnectInboxPrompt from './components/connect-inbox-prompt.js';

//  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  - 
// Lazy-loaded pages (code splitting)
//  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  - 

const Login = lazy(() => import('./pages/login.js'));
const SetPassword = lazy(() => import('./pages/set-password.js'));
const Dashboard = lazy(() => import('./pages/dashboard.js'));
const Inbox = lazy(() => import('./pages/inbox.js'));
const ExternalConversationsWorkspace = lazy(() => import('./pages/external-conversations-workspace.js'));
const Deals = lazy(() => import('./pages/deals.js'));
const DealDetail = lazy(() => import('./pages/deal-detail.js'));
const BidsPage = lazy(() => import('./pages/bids.js'));
const PartyDetail = lazy(() => import('./pages/party-detail.js'));
const Contacts = lazy(() => import('./pages/contacts.js'));
const Calendar = lazy(() => import('./pages/calendar.js'));
const MapPage = lazy(() => import('./pages/map.js'));
const AiWorkforce = lazy(() => import('./pages/ai-workforce.js'));
const VoiceProfileBuilder = lazy(() => import('./pages/voice-profile-builder.js'));
const BatchCreator = lazy(() => import('./pages/batch-creator.js'));
const BatchCardStack = lazy(() => import('./pages/batch-card-stack.js'));
const BatchSendConfirm = lazy(() => import('./pages/batch-send-confirm.js'));
const OperatorDoor = lazy(() => import('./pages/operator-door.js'));
const ParcelWorkCockpit = lazy(() => import('./pages/parcel-work-cockpit.js'));

// Admin
const ConnectedAccounts = lazy(() => import('./pages/admin/connected-accounts.js'));
const Automation = lazy(() => import('./pages/admin/automation.js'));
const Webhooks = lazy(() => import('./pages/admin/webhooks.js'));
const Audit = lazy(() => import('./pages/admin/audit.js'));
const AiCalls = lazy(() => import('./pages/admin/ai-calls.js'));
const Wiring = lazy(() => import('./pages/admin/wiring.js'));
const Digest = lazy(() => import('./pages/admin/digest.js'));
const SettingsHub = lazy(() => import('./pages/admin/settings.js'));
const AdminAiEmployees = lazy(() => import('./pages/admin/ai-employees.js'));
const AdminUsers = lazy(() => import('./pages/admin/users.js'));
const AdminMaintenance = lazy(() => import('./pages/admin/maintenance.js'));
const AdminFreeHouse = lazy(() => import('./pages/admin/free-house.js'));
const AnswerEngineDashboard = lazy(() => import('./pages/admin/answer-engine.js'));
const SellerPortalDashboard = lazy(() => import('./pages/admin/seller-portal.js'));
const Autonomy = lazy(() => import('./pages/admin/autonomy.js'));
const NeoActivity = lazy(() => import('./pages/admin/neo-activity.js'));
const Workspaces = lazy(() => import('./pages/admin/workspaces.js'));

// Saved view permalink
const SavedViewPermalink = lazy(() => import('./pages/v/[slug].js'));

// Portal pages
const PortalMagic = lazy(() => import('./pages/portal/magic.js'));
const PortalHome = lazy(() => import('./pages/portal/home.js'));
const PortalDocuments = lazy(() => import('./pages/portal/documents.js'));
const PortalSignature = lazy(() => import('./pages/portal/signature.js'));
const PortalPhotos = lazy(() => import('./pages/portal/photos.js'));
const PortalMessages = lazy(() => import('./pages/portal/messages.js'));

//  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  - 
// Loading fallback
//  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  - 

function PageSkeleton() {
  return (
    <div className="flex items-center justify-center h-screen w-full">
      <div className="flex flex-col gap-3 w-full max-w-md p-6">
        <div className="shimmer h-8 rounded-lg w-3/4" />
        <div className="shimmer h-4 rounded w-full" />
        <div className="shimmer h-4 rounded w-5/6" />
        <div className="shimmer h-4 rounded w-4/6" />
      </div>
    </div>
  );
}

//  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  - 
// Auth guard
//  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  - 

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) return <PageSkeleton />;
  if (!isAuthenticated) return <Redirect to="/login" />;

  return <>{children}</>;
}

function EmbeddedInboxRoute() {
  const { isEmbedded, isStandalone, bridgeToken } = useEmbeddedMode();

  if (!isStandalone && (!isEmbedded || !bridgeToken)) {
    return <PageSkeleton />;
  }

  // chromeless: LA-AI is the host here and already provides navigation, search
  // and an assistant. Shipping ours would put a second brand header, a second
  // global search and a second Neo inside someone else's app - and those
  // controls sit on a read-only bridge, so they could not work anyway.
  return (
    <RequireAuth>
      <Inbox chromeless />
    </RequireAuth>
  );
}

function EmbeddedWorkspaceRoute() {
  const { isEmbedded, isStandalone, bridgeToken } = useEmbeddedMode();

  if (!isStandalone && (!isEmbedded || !bridgeToken)) {
    return <PageSkeleton />;
  }

  return (
    <RequireAuth>
      <ExternalConversationsWorkspace />
    </RequireAuth>
  );
}

//  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  - 
// Portal detection
//  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  - 

function isPortalSubdomain() {
  const host = window.location.hostname;
  return host.startsWith('portal.') || window.location.pathname.startsWith('/portal');
}

//  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  - 
// App
//  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  - 

export default function App() {
  return (
    <TenantProvider>
      <TooltipProvider delayDuration={400} skipDelayDuration={0}>
        {/* Skip link for keyboard users (item #100) */}
        <a href="#main-content" className="skip-link">Skip to content</a>
        <Suspense fallback={<PageSkeleton />}>
          {isPortalSubdomain() ? (
            <PortalRouter />
          ) : (
            <BrowserCallProvider>
              <MainRouter />
              {/* Outside the router so it survives navigation; it renders
                  nothing unless a signed-in teammate has no inbox at all. */}
              <ConnectInboxPrompt />
            </BrowserCallProvider>
          )}
        </Suspense>
      </TooltipProvider>
    </TenantProvider>
  );
}

function MainRouter() {
  return (
    <Switch>
      {/* Public */}
      <Route path="/login" component={Login} />
      <Route path="/set-password" component={SetPassword} />

      {/* Saved view permalink (no auth required for shared views) */}
      <Route path="/v/:slug">
        {(params) => <SavedViewPermalink slug={params.slug} />}
      </Route>

      <Route path="/operator-door/:token">
        {(params) => <OperatorDoor token={params.token} />}
      </Route>

      {/* Protected app routes */}
      <Route path="/">
        {() => (
          <RequireAuth>
            <Redirect to="/inbox" />
          </RequireAuth>
        )}
      </Route>
      <Route path="/inbox/:messageId">
        {(params) => <RequireAuth><Inbox initialMessageId={params.messageId} /></RequireAuth>}
      </Route>
      <Route path="/inbox">
        {() => <RequireAuth><Inbox /></RequireAuth>}
      </Route>
      <Route path="/embed/inbox">
        {() => <EmbeddedInboxRoute />}
      </Route>
      <Route path="/embed/workspace">
        {() => <EmbeddedWorkspaceRoute />}
      </Route>
      <Route path="/deals">
        {() => <RequireAuth><Deals /></RequireAuth>}
      </Route>
      <Route path="/bids">
        {() => <RequireAuth><AppShell><BidsPage /></AppShell></RequireAuth>}
      </Route>
      <Route path="/deals/new">
        {() => <RequireAuth><AppShell><DealDetail /></AppShell></RequireAuth>}
      </Route>
      <Route path="/deals/:id">
        {(params) => <RequireAuth><AppShell><DealDetail dealId={params.id} /></AppShell></RequireAuth>}
      </Route>
      <Route path="/parties/:id">
        {() => <RequireAuth><PartyDetail /></RequireAuth>}
      </Route>
      <Route path="/contacts">
        {() => <RequireAuth><Contacts /></RequireAuth>}
      </Route>
      <Route path="/calendar">
        {() => <RequireAuth><Calendar /></RequireAuth>}
      </Route>
      <Route path="/planner">
        {() => <RequireAuth><Calendar /></RequireAuth>}
      </Route>
      <Route path="/map">
        {() => <RequireAuth><AppShell><MapPage /></AppShell></RequireAuth>}
      </Route>
      <Route path="/parcel-work">
        {() => <RequireAuth><AppShell><ParcelWorkCockpit /></AppShell></RequireAuth>}
      </Route>
      <Route path="/voice-profiles/new">
        {() => <RequireAuth><VoiceProfileBuilder /></RequireAuth>}
      </Route>
      <Route path="/voice-profiles/:id/edit">
        {(params) => <RequireAuth><VoiceProfileBuilder profileId={params.id} /></RequireAuth>}
      </Route>
      <Route path="/batches/new">
        {() => <RequireAuth><BatchCreator /></RequireAuth>}
      </Route>
      <Route path="/batches/:id/review">
        {(params) => <RequireAuth><BatchCardStack batchId={params.id} /></RequireAuth>}
      </Route>
      <Route path="/batches/:id/confirm">
        {(params) => <RequireAuth><BatchSendConfirm batchId={params.id} /></RequireAuth>}
      </Route>
      <Route path="/ai-workforce/voice-profiles">
        {() => <RequireAuth><AiWorkforce activeTab="voice" /></RequireAuth>}
      </Route>
      <Route path="/ai-workforce/batches">
        {() => <RequireAuth><AiWorkforce activeTab="batches" /></RequireAuth>}
      </Route>
      <Route path="/ai-workforce">
        {() => <RequireAuth><AiWorkforce activeTab="employees" /></RequireAuth>}
      </Route>
      <Route path="/ai-employees">
        {() => <RequireAuth><AiWorkforce activeTab="employees" /></RequireAuth>}
      </Route>
      <Route path="/voice-profiles">
        {() => <RequireAuth><AiWorkforce activeTab="voice" /></RequireAuth>}
      </Route>
      <Route path="/batches">
        {() => <RequireAuth><AiWorkforce activeTab="batches" /></RequireAuth>}
      </Route>

      {/* Admin routes */}
      <Route path="/admin/settings">
        {() => <RequireAuth><SettingsHub /></RequireAuth>}
      </Route>
      <Route path="/admin/ai-employees">
        {() => <RequireAuth><AdminAiEmployees /></RequireAuth>}
      </Route>
      <Route path="/admin/users">
        {() => <RequireAuth><AdminUsers /></RequireAuth>}
      </Route>
      <Route path="/admin/maintenance">
        {() => <RequireAuth><AdminMaintenance /></RequireAuth>}
      </Route>
      <Route path="/admin/free-house">
        {() => <RequireAuth><AdminFreeHouse /></RequireAuth>}
      </Route>
      <Route path="/admin/connected-accounts">
        {() => <RequireAuth><ConnectedAccounts /></RequireAuth>}
      </Route>
      <Route path="/admin/automation">
        {() => <RequireAuth><Automation /></RequireAuth>}
      </Route>
      <Route path="/admin/webhooks">
        {() => <RequireAuth><Webhooks /></RequireAuth>}
      </Route>
      <Route path="/admin/audit">
        {() => <RequireAuth><Audit /></RequireAuth>}
      </Route>
      <Route path="/admin/ai-calls">
        {() => <RequireAuth><AiCalls /></RequireAuth>}
      </Route>
      <Route path="/admin/wiring">
        {() => <RequireAuth><Wiring /></RequireAuth>}
      </Route>
      <Route path="/admin/digest">
        {() => <RequireAuth><Digest /></RequireAuth>}
      </Route>
      <Route path="/admin/autonomy">
        {() => <RequireAuth><Autonomy /></RequireAuth>}
      </Route>
      <Route path="/admin/neo-activity">
        {() => <RequireAuth><NeoActivity /></RequireAuth>}
      </Route>
      <Route path="/admin/workspaces">
        {() => <RequireAuth><Workspaces /></RequireAuth>}
      </Route>

      <Route path="/admin/answer-engine">
        {() => <RequireAuth><AnswerEngineDashboard /></RequireAuth>}
      </Route>
      <Route path="/admin/seller-portal">
        {() => <RequireAuth><SellerPortalDashboard /></RequireAuth>}
      </Route>

      {/* 404 */}
      <Route>
        {() => (
          <div className="flex items-center justify-center h-screen flex-col gap-4">
            <h1 className="text-2xl font-semibold text-ink">Page not found</h1>
            <a href="/inbox" className="text-brand hover:underline">Go to inbox</a>
          </div>
        )}
      </Route>
    </Switch>
  );
}

function PortalRouter() {
  return (
    <Switch>
      <Route path="/portal/magic/:token">
        {(params) => <PortalMagic token={params.token} />}
      </Route>
      <Route path="/portal">
        {() => <PortalHome />}
      </Route>
      <Route path="/portal/documents">
        {() => <PortalDocuments />}
      </Route>
      <Route path="/portal/signature">
        {() => <PortalSignature />}
      </Route>
      <Route path="/portal/photos">
        {() => <PortalPhotos />}
      </Route>
      <Route path="/portal/messages">
        {() => <PortalMessages />}
      </Route>
      <Route>
        {() => <Redirect to="/portal" />}
      </Route>
    </Switch>
  );
}
